"use client";

import { useProjectText } from "@/features/project/useProjectText";
import type { ItemProperties, POIPageContent } from "@/types";
import { formatCell, tableEntries } from "../../poiTable";

/**
 * A tabular POI block: one row per entry of an array-valued property.
 *
 * Column headers come from the tenant's own strings under `table.column.*`,
 * falling back to the raw column name — a table of unlabelled columns is
 * unreadable, and the name is at least the thing the tenant typed in the CMS.
 */
export function POITable({
  properties,
  options,
}: {
  properties: ItemProperties;
  options: POIPageContent;
}) {
  const t = useProjectText();
  const columns = options.tableColumns ?? [];
  const rows = tableEntries(properties, options.field, columns);

  if (rows.length === 0) return null;

  return (
    <div className="mb-3 mt-3 overflow-x-auto">
      <table className="min-w-full text-left text-sm text-gray-500">
        <thead className="bg-gray-700 text-gray-300">
          <tr>
            {columns.map((column) => (
              <th key={column} className="p-2 font-medium tracking-wider text-white">
                {t(`table.column.${column}`, column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {rows.map((row, index) => (
            <tr key={String(row.id ?? index)} className="hover:bg-gray-50">
              {columns.map((column) => (
                <td key={column} className="p-2">
                  {String(formatCell(row[column]) ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
